import { Icon } from '../../../../widgets/icon';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import MicrophoneIcon from 'src/shared/material_icons/legacy/microphone-with-gradient.svg';
import styled from 'styled-components';
import { bounce } from '../../styles/animations';

const InputContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;
    min-height: 50px;
    box-sizing: border-box;
    background: ${(props) => props.theme.app.backgroundColorBase};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    transition: border-color 0.2s ease;

    &:focus-within {
        border-color: #2196f3;
    }
`;

const UploadButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.textColor};
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => props.theme.app.primary_light};
        color: ${(props) => props.theme.app.primary};
    }

    svg {
        width: 18px;
        height: 18px;
        fill: currentColor;
    }
`;

const HiddenFileInput = styled.input`
    display: none;
`;

const TextInput = styled.textarea`
    flex: 1;
    border: none;
    background: transparent;
    font-size: 14px;
    color: ${(props) => props.theme.app.textColor};
    outline: none;
    resize: none;
    overflow: hidden;
    min-height: 24px;
    max-height: 120px;
    overflow-y: auto;
    line-height: 1.5;
    font-family: inherit;
    padding: 0;
    align-self: center;

    &::placeholder {
        color: ${(props) => props.theme.app.textColor};
        opacity: 0.5;
    }
`;

const UploadedImagePreview = styled.div`
    position: relative;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
`;

const PreviewImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const RemoveButton = styled.button`
    position: absolute;
    top: -4px;
    right: -4px;
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #ef4444;
    color: white;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #dc2626;
    }
`;

const MicButton = styled.button<{ $isRecording: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: none;
    border-radius: 16px;
    background: ${(props) => (props.$isRecording ? 'white' : 'white')};
    color: ${(props) => (props.$isRecording ? '#ffffff' : '#6b7280')};
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: ${(props) => (props.$isRecording ? '#fdd' : '#eaeaea')};
        color: ${(props) => (props.$isRecording ? '#ffffff' : '#374151')};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        min-width: 0;
        fill: currentColor;

        svg {
            width: 24px;
            height: 24px;
        }
    }
`;

// animated dots components

const SvgDotsWrapper = styled.svg`
    .dot-1 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        animation-delay: 0s;
        transform-origin: center;
    }
    .dot-2 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        animation-delay: 0.15s;
        transform-origin: center;
    }
    .dot-3 {
        animation: ${bounce} 0.75s ease-in-out infinite;
        animation-delay: 0.3s;
        transform-origin: center;
    }
`;

const AnimatedListeningDots: FC = () => (
    <SvgDotsWrapper xmlns='http://www.w3.org/2000/svg' viewBox='0 -960 960 960'>
        <defs>
            <linearGradient id='animated-dots-gradient' x1='160' y1='-480' x2='800' y2='-480' gradientUnits='userSpaceOnUse'>
                <stop stopColor='#3B82F6' />
                <stop offset='1' stopColor='#8B5CF6' />
            </linearGradient>
        </defs>
        <circle className='dot-1' cx='240' cy='-320' r='80' fill='url(#animated-dots-gradient)' />
        <circle className='dot-2' cx='480' cy='-320' r='80' fill='url(#animated-dots-gradient)' />
        <circle className='dot-3' cx='720' cy='-320' r='80' fill='url(#animated-dots-gradient)' />
    </SvgDotsWrapper>
);

// Type declarations for Web Speech API
interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
    resultIndex: number;
}

interface SpeechRecognitionResultList {
    length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
    length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
    isFinal: boolean;
}

interface SpeechRecognitionAlternative {
    transcript: string;
    confidence: number;
}

interface SpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onerror: ((event: Event) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface SpeechRecognitionConstructor {
    new (): SpeechRecognition;
}
declare global {
    interface Window {
        SpeechRecognition?: SpeechRecognitionConstructor;
        webkitSpeechRecognition?: SpeechRecognitionConstructor;
    }
}

interface PromptInputProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    maxLength?: number;
    uploadedImageUrl?: string;
    onImageUpload?: (file: File) => void;
    onImageRemove?: () => void;
    disabled?: boolean;
}

export const PromptInput: FC<PromptInputProps> = ({
    value,
    onChange,
    placeholder,
    maxLength = 500,
    uploadedImageUrl,
    onImageUpload,
    onImageRemove,
    disabled = false
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [speechSupported, setSpeechSupported] = useState(false);
    const recognitionRef = useRef<SpeechRecognition | null>(null);

    // Check if speech recognition is supported
    useEffect(() => {
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        setSpeechSupported(!!SpeechRecognitionAPI);
    }, []);

    // Auto-resize textarea to fit content
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [value]);

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file && onImageUpload) {
                onImageUpload(file);
            }
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [onImageUpload]
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files?.[0];
            if (file && file.type.startsWith('image/') && onImageUpload) {
                onImageUpload(file);
            }
        },
        [onImageUpload]
    );

    const startRecording = useCallback(() => {
        const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognitionAPI) return;

        const recognition = new SpeechRecognitionAPI();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = navigator.language || 'en-US';

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let finalTranscript = '';
            let interimTranscript = '';

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (result.isFinal) {
                    finalTranscript += result[0].transcript;
                } else {
                    interimTranscript += result[0].transcript;
                }
            }

            if (finalTranscript) {
                const newValue = value + (value ? ' ' : '') + finalTranscript;
                onChange(newValue.slice(0, maxLength));
            }
        };

        recognition.onerror = () => {
            setIsRecording(false);
            recognitionRef.current = null;
        };

        recognition.onend = () => {
            setIsRecording(false);
            recognitionRef.current = null;
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsRecording(true);
    }, [value, onChange, maxLength]);

    const stopRecording = useCallback(() => {
        if (recognitionRef.current) {
            recognitionRef.current.stop();
            recognitionRef.current = null;
        }
        setIsRecording(false);
    }, []);

    const toggleRecording = useCallback(() => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    }, [isRecording, startRecording, stopRecording]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    return (
        <InputContainer
            style={{ borderColor: isDragOver ? '#2196f3' : undefined }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {onImageUpload && !uploadedImageUrl && (
                <>
                    <UploadButton onClick={handleUploadClick} disabled={disabled} title={T._('Upload image', 'Admin')}>
                        <Icon>
                            <ImageIcon />
                        </Icon>
                    </UploadButton>
                    <HiddenFileInput ref={fileInputRef} type='file' accept='image/*' onChange={handleFileChange} />
                </>
            )}
            {uploadedImageUrl && (
                <UploadedImagePreview>
                    <PreviewImage src={uploadedImageUrl} alt={T._('Uploaded', 'Admin')} />
                    {onImageRemove && (
                        <RemoveButton onClick={onImageRemove} title={T._('Remove image', 'Admin')}>
                            ×
                        </RemoveButton>
                    )}
                </UploadedImagePreview>
            )}
            <TextInput
                ref={textareaRef}
                rows={1}
                value={value}
                onChange={(e) => onChange(e.target.value.slice(0, maxLength))}
                placeholder={placeholder || T._('Describe what you want to create...', 'Admin')}
                disabled={disabled}
            />
            {speechSupported && (
                <MicButton
                    $isRecording={isRecording}
                    onClick={toggleRecording}
                    disabled={disabled}
                    title={isRecording ? T._('Stop recording', 'Admin') : T._('Start voice input', 'Admin')}
                >
                    <Icon>{isRecording ? <AnimatedListeningDots /> : <MicrophoneIcon />}</Icon>
                </MicButton>
            )}
        </InputContainer>
    );
};

export default PromptInput;
